import { Component, computed, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationExtras, Router, RouterModule } from '@angular/router';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatCardModule } from '@angular/material/card';
import { AiAssistantI18nModule } from '../../assets/i18n/ai-assistant-i18n.module';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { AiConnectionsForm, ConnectionMetadata } from '../../core/forms';
import { buildUrlWithQueryParams } from '../../core/utils/navigation.helper';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { ACCOUNT_GROUP_ID_TOKEN, PLATFORM_LOCATION_TOKEN } from '../../core/tokens';
import { PlatformLocation } from '../../core/ai-assistant.constants';

@Component({
  selector: 'ai-connections-form',
  imports: [
    AiAssistantI18nModule,
    MatCardModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    FormsModule,
    MatCheckboxModule,
    GalaxyLoadingSpinnerModule,
    GalaxyTooltipModule,
    MatIconModule,
    RouterModule,
    MatButtonModule,
  ],
  templateUrl: './ai-connections-form.component.html',
  styleUrl: './ai-connections-form.component.scss',
})
export class AiConnectionsFormComponent {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly platformLocation = inject(PLATFORM_LOCATION_TOKEN);
  private readonly accountGroupId = toSignal(inject(ACCOUNT_GROUP_ID_TOKEN));

  connectionForms = input.required<AiConnectionsForm>();
  triggerFeatureFlagEnabled = input<boolean>(false);
  showManageButton = input<boolean>(true); 

  protected readonly showAllConnections = signal(false);
  private readonly CONNECTIONS_LIMIT = 4;

  protected readonly displayedConnections = computed(() => {
    const connections = this.connectionForms().controls;
    
    const sortedConnections = [...connections].sort((a, b) => {
      const aConnected = a.controls.connected.value ?? false;
      const bConnected = b.controls.connected.value ?? false;
      
      return Number(bConnected) - Number(aConnected);
    });
    
    if (this.showAllConnections() || sortedConnections.length <= this.CONNECTIONS_LIMIT) {
      return sortedConnections;
    }
    return sortedConnections.slice(0, this.CONNECTIONS_LIMIT);
  });

  protected readonly shouldShowToggleButton = computed(() => {
    return this.connectionForms().controls.length > this.CONNECTIONS_LIMIT;
  });

  protected readonly toggleButtonText = computed(() => {
    return this.showAllConnections() 
      ? 'AI_ASSISTANT.SETTINGS.CHANNELS.VIEW_LESS' 
      : 'AI_ASSISTANT.SETTINGS.CHANNELS.VIEW_MORE';
  });

  protected toggleShowAllConnections(): void {
    this.showAllConnections.set(!this.showAllConnections());
  }

  protected readonly manageChannelsRoute = computed(() => {
    if (this.platformLocation === PlatformLocation.PLATFORM_LOCATION_BUSINESS_APP) {
      return `/account/location/${this.accountGroupId()}/administration/inbox`;
    } else {
      return '/inbox/settings';
    }
  });

  protected triggerAction(metadata: ConnectionMetadata): void {
    if (metadata.cta?.action) {
      this.buildConnectionActionCallback(metadata)?.();
    }
  }

  protected buildConnectionActionCallback(metadata: ConnectionMetadata): (() => void) | undefined {
    if (!metadata?.cta?.action?.showButton) {
      return undefined;
    }

    const action = metadata.cta.action;

    if (action?.url) {
      if (action.newTab && !action.relativeRoute) {
        const finalUrl = buildUrlWithQueryParams(action.url, action.queryParams);
        return () => window.open(finalUrl, '_blank');
      }

      const navigationExtras: NavigationExtras = action.relativeRoute ? { relativeTo: this.route } : {};
      if (action.queryParams) {
        navigationExtras.queryParams = action.queryParams;
      }

      return () => this.router.navigate([action.url], navigationExtras);
    }

    if (action?.pathCommands) {
      return () => this.router.navigate(action.pathCommands);
    }

    if (action?.callback) {
      return action.callback;
    }
    return undefined;
  }
}
